import type { MusicProvider, Track, PlayerState } from "@/types/music-provider";

declare global {
  interface Window {
    MusicKit: any;
  }
}

export class AppleMusicProvider implements MusicProvider {
  type = 'apple' as const;
  name = 'Apple Music';
  isAuthenticated = false;
  isReady = false;
  
  private musicKit: any = null;
  private player: any = null;
  private playerState: PlayerState = {
    isPlaying: false,
    currentTrack: null,
    position: 0,
    duration: 0,
    volume: 100
  };

  async login(): Promise<void> {
    await this.initializeMusicKit();
    if (this.musicKit) {
      await this.musicKit.authorize();
      this.isAuthenticated = this.musicKit.isAuthorized;
    }
  }

  async logout(): Promise<void> {
    if (this.musicKit) {
      await this.musicKit.unauthorize();
      this.isAuthenticated = false;
      this.isReady = false;
    }
  }

  async checkAuthStatus(): Promise<boolean> {
    await this.initializeMusicKit();
    this.isAuthenticated = this.musicKit?.isAuthorized || false;
    return this.isAuthenticated;
  }

  async play(trackId?: string): Promise<void> {
    if (!this.musicKit) throw new Error('Apple Music não inicializado');
    
    if (trackId) {
      await this.musicKit.setQueue({ song: trackId });
    }
    await this.musicKit.play();
    this.playerState.isPlaying = true;
  }

  async pause(): Promise<void> {
    if (this.musicKit) {
      await this.musicKit.pause();
      this.playerState.isPlaying = false;
    }
  }

  async resume(): Promise<void> {
    await this.play();
  }

  async next(): Promise<void> {
    if (this.musicKit) {
      await this.musicKit.skipToNextItem();
    }
  }

  async previous(): Promise<void> {
    if (this.musicKit) {
      await this.musicKit.skipToPreviousItem();
    }
  }

  async seek(positionMs: number): Promise<void> {
    if (this.musicKit) {
      await this.musicKit.seekToTime(positionMs / 1000);
    }
  }

  async setVolume(volume: number): Promise<void> {
    if (this.musicKit) {
      this.musicKit.volume = volume / 100;
      this.playerState.volume = volume;
    }
  }

  getPlayerState(): PlayerState {
    return this.playerState;
  }

  getCurrentTrack(): Track | null {
    if (!this.musicKit?.nowPlayingItem) return null;
    
    const item = this.musicKit.nowPlayingItem;
    return {
      id: item.id,
      name: item.attributes.name,
      artist: item.attributes.artistName,
      album: item.attributes.albumName,
      album_art: item.attributes.artwork?.url.replace('{w}', '300').replace('{h}', '300'),
      duration_ms: item.attributes.durationInMillis,
      external_url: item.attributes.url
    };
  }

  async searchTracks(query: string, limit = 20): Promise<Track[]> {
    if (!this.musicKit) return [];
    
    const results = await this.musicKit.api.search(query, { types: 'songs', limit });
    return results.songs.data.map((song: any) => ({
      id: song.id,
      name: song.attributes.name,
      artist: song.attributes.artistName,
      album: song.attributes.albumName,
      album_art: song.attributes.artwork?.url.replace('{w}', '300').replace('{h}', '300'),
      duration_ms: song.attributes.durationInMillis,
      external_url: song.attributes.url
    }));
  }

  async getRecommendations(seedTracks: string[], targetBPM?: number): Promise<Track[]> {
    // Apple Music não tem API pública de recomendações baseadas em BPM
    // Retorna busca genérica por enquanto
    return this.searchTracks('workout running', 50);
  }

  private async initializeMusicKit(): Promise<void> {
    if (this.musicKit) return;

    // Carregar MusicKit SDK
    if (!window.MusicKit) {
      const script = document.createElement('script');
      script.src = 'https://js-cdn.music.apple.com/musickit/v3/musickit.js';
      script.async = true;
      document.head.appendChild(script);
      
      await new Promise((resolve) => {
        script.onload = resolve;
      });
    }

    // Inicializar (requer developer token - deve ser configurado pelo usuário)
    this.musicKit = await window.MusicKit.configure({
      developerToken: 'YOUR_APPLE_MUSIC_DEVELOPER_TOKEN',
      app: {
        name: 'You Better Run',
        build: '1.0.0'
      }
    });

    this.isReady = true;
  }
}
